import { get, toString } from 'lodash';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import React from 'react';
import { useCombobox, UseComboboxProps } from 'downshift';
import { ChevronDown } from 'styled-icons/fa-solid';

import { getLayoutProps, getSearchStringRegExp } from 'src/helpers';
import { Label, LabelProps } from '../Label';
import { BaseStyledInput, StyledInputProps } from '../StyledInput';
import { InputLoader } from '../InputLoader';
import { useComboboxPositioning } from './useComboboxPositioning';
import { StyledContainer, StyledContainerProps, StyledComboboxControl } from './Combobox.styled';
import { Menu } from './Menu';

type AllComboboxProps = StyledContainerProps &
  UseComboboxProps<any> &
  LabelProps &
  StyledInputProps & {
    shape?: { valueKey: string; labelKey: string };
    children?: React.ReactNode;
    error?: boolean | string;
    onChange?: (selectedItem: any) => void;
    loadItems: ({ params: { q: string } }) => Promise<any>;
    placement?: string;
    ignoreCached?: boolean;
  };

export type ComboboxProps = RequireOnlyOne<AllComboboxProps, 'items' | 'loadItems'>;

export const Combobox = React.forwardRef(
  (
    {
      children,
      name,
      value,
      defaultValue,
      items,
      loadItems,
      onChange,
      onBlur,
      label,
      placeholder,
      disabled,
      variant,
      size,
      shape: { valueKey, labelKey },
      error,
      placement,
      ignoreCached,
      ...props
    }: ComboboxProps,
    outerRef: (node: HTMLElement | null) => void,
  ) => {
    const { referenceElementRef, popperElementRef, styles, attributes } = useComboboxPositioning({ placement });
    const getItemValue = React.useCallback((item) => toString(get(item, valueKey, item)), [valueKey]);
    const getItemLabel = React.useCallback((item) => toString(get(item, labelKey, item)), [labelKey]);
    const debouncedLoadItems = React.useCallback(debounceLoadItems(loadItems), []);
    const [inputValue, setInputValue] = React.useState(getItemLabel(value) || '');
    const [inputItems, setInputItems] = React.useState(items);
    const [isInitiallyOpened, setIsInitiallyOpened] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const cacheItems = React.useRef({}).current;

    React.useEffect(() => {
      if (ignoreCached) {
        setInputItems(items);
      }
    }, [items]);

    React.useEffect(() => {
      setInputValue(getItemLabel(value) || '');
    }, [getItemLabel, value]);

    const searchStringRegExp = React.useMemo(
      () => getSearchStringRegExp(isInitiallyOpened ? '' : inputValue),
      [inputValue, isInitiallyOpened],
    );

    const filterItemsBySearchQuery = React.useCallback(
      (acc, item) => {
        const itemLabel = getItemLabel(item).trim().toLowerCase();
        const index = itemLabel.search(searchStringRegExp);
        if (index !== -1) {
          acc.push({ item, index });
        }
        return acc;
      },
      [getItemLabel, searchStringRegExp],
    );

    const menuItems = React.useMemo(
      () =>
        inputItems
          .reduce((acc, item) => {
            if (item.items) {
              const filteredItems = item.items
                .reduce(filterItemsBySearchQuery, [])
                .sort((item1, item2) => item1.index - item2.index)
                .map(({ item }) => item);

              if (filteredItems.length) {
                acc.push({
                  ...item,
                  items: filteredItems,
                });
              }
              return acc;
            }
            return filterItemsBySearchQuery(acc, item);
          }, [])
          .sort((item1, item2) => item1.index - item2.index)
          .map((item) => (item.item ? item.item : item)),
      [filterItemsBySearchQuery, inputItems],
    );

    const flatMenuItems = menuItems.flatMap((item) => item.items || item);

    const [layoutProps, restStyledProps, restProps] = getLayoutProps(props);
    const {
      getComboboxProps,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      isOpen,
      highlightedIndex,
    } = useCombobox({
      selectedItem: value,
      items: flatMenuItems,
      inputValue,
      stateReducer: (state, actions) => {
        switch (actions.type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputBlur: {
            return {
              ...actions.changes,
              inputValue: getItemLabel(actions.changes.selectedItem),
            };
          }

          default:
            return actions.changes;
        }
      },
      onStateChange: async ({ type, inputValue = '', selectedItem }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputChange: {
            setIsInitiallyOpened(false);
            setInputValue(inputValue);
            if (loadItems) {
              if (cacheItems[inputValue]) {
                setInputItems(cacheItems[inputValue]);
                // } else if (inputValue.length > 1) {
              } else if (inputValue.length > 1 || inputValue.length === 0) {
                setIsLoading(true);
                try {
                  const items = await debouncedLoadItems(inputValue);
                  cacheItems[inputValue] = items;
                  if (items) {
                    setInputItems(items);
                  }
                } finally {
                  setIsLoading(false);
                }
              }
            }
            break;
          }

          case useCombobox.stateChangeTypes.ToggleButtonClick: {
            setIsInitiallyOpened(true);
            if (loadItems && !inputItems.length) {
              setIsLoading(true);
              try {
                const items = await debouncedLoadItems('');
                cacheItems[''] = items;
                if (items) {
                  setInputItems(items);
                }
              } finally {
                setIsLoading(false);
              }
            }
            break;
          }

          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick: {
            if (selectedItem && onChange) {
              onChange(selectedItem);
            }
            break;
          }

          case useCombobox.stateChangeTypes.InputBlur: {
            setInputValue(getItemLabel(value) || '');
            if (onBlur) {
              onBlur();
            }
            break;
          }

          default:
            break;
        }
      },
      ...restProps,
    });

    React.useEffect(() => {
      if (isOpen && loadItems && !inputItems.length && !inputValue) {
        (async () => {
          setIsLoading(true);
          try {
            const items = await debouncedLoadItems('');
            cacheItems[''] = items;
            if (items) {
              setInputItems(items);
            }
          } finally {
            setIsLoading(false);
          }
        })();
      }
    }, [isOpen, loadItems, inputItems.length, inputValue, debouncedLoadItems, cacheItems]);

    return (
      <StyledContainer ref={outerRef} {...layoutProps}>
        <Label as="label" label={label} disabled={disabled} {...getLabelProps()} />
        <StyledComboboxControl
          {...{ isOpen, variant, size, error, ...restStyledProps }}
          {...getComboboxProps()}
          {...getToggleButtonProps({ ref: referenceElementRef, disabled })}
        >
          {!isLoading && <ChevronDown className="chevron" size={10} />}
          {isLoading && <InputLoader size={size} />}
          <BaseStyledInput
            {...getInputProps({ name, placeholder, disabled })}
            className="search-input"
            variant="ghost"
            data-testid="combobox_input"
          />
          <div
            ref={popperElementRef}
            style={styles.popper}
            {...attributes.popper}
            css={{ zIndex: 1000, width: '100%' }}
          >
            {!isLoading && isOpen && (
              <Menu
                {...{
                  highlightedIndex,
                  getMenuProps,
                  getItemProps,
                  getItemValue,
                  getItemLabel,
                  items: menuItems,
                  size,
                }}
              />
            )}
          </div>
        </StyledComboboxControl>
        {children}
      </StyledContainer>
    );
  },
);

Combobox.defaultProps = {
  items: [],
  variant: 'primary',
  size: 'md',
  shape: { valueKey: '', labelKey: '' },
  placeholder: 'Select an option',
};

Combobox.displayName = 'Combobox';

const debounceLoadItems = (loadItems: (...params: any) => Promise<any> = async () => {}) =>
  awesomeDebouncePromise((inputValue: string) => loadItems({ params: { q: inputValue.trim() } }), 250);
